import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';

interface MessagesPageProps {
  onNavigate: (page: string, data?: any) => void;
  user?: any;
  initialConversationId?: number;
  initialUsername?: string;
}

export default function MessagesPage({ onNavigate, user, initialConversationId, initialUsername }: MessagesPageProps) {
  const [conversations, setConversations] = useState<any[]>([]);
  const [activeConvoId, setActiveConvoId] = useState<number | null>(initialConversationId || null);
  const [messages, setMessages] = useState<any[]>([]);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newConvoUsername, setNewConvoUsername] = useState('');
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [newConvoError, setNewConvoError] = useState('');
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [showConvoMenu, setShowConvoMenu] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const loadConversations = useCallback(async () => {
    try {
      const data = await api.messages.conversations();
      setConversations(data);
    } catch (err) {
      console.error('Load conversations error:', err);
    }
  }, []);

  const loadMessages = useCallback(async (convoId: number) => {
    try {
      const data = await api.messages.conversation(convoId);
      setMessages(data.messages);
      setOtherUser(data.conversation.otherUser);
      setActiveConvoId(convoId);
      setMobileShowChat(true);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Load messages error:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    const init = async () => {
      setLoading(true);
      await loadConversations();

      if (initialUsername) {
        try {
          const result = await api.messages.start(initialUsername);
          await loadMessages(result.conversationId);
        } catch (err) {
          console.error('Start conversation error:', err);
        }
      } else if (initialConversationId) {
        await loadMessages(initialConversationId);
      }
      setLoading(false);
    };
    init();
  }, [user, initialConversationId, initialUsername]);

  useEffect(() => {
    if (!user || !activeConvoId) return;
    pollRef.current = setInterval(async () => {
      try {
        const data = await api.messages.conversation(activeConvoId);
        setMessages(data.messages);
      } catch {}
      loadConversations();
    }, 8000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, activeConvoId, loadConversations]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const msg = await api.messages.send({
        conversationId: activeConvoId!,
        body: newMessage.trim(),
      });
      setMessages(prev => [...prev, msg]);
      setNewMessage('');
      loadConversations();
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStartConvo = async () => {
    if (!newConvoUsername.trim()) return;
    setNewConvoError('');
    try {
      const result = await api.messages.start(newConvoUsername.trim());
      setShowNewConvo(false);
      setNewConvoUsername('');
      await loadConversations();
      await loadMessages(result.conversationId);
    } catch (err: any) {
      setNewConvoError(err.message || 'User not found');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  function timeAgo(date: string) {
    const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 604800) return `${Math.floor(s / 86400)}d`;
    return new Date(date).toLocaleDateString();
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-4">✉️</div>
        <h2 className="text-2xl font-bold mb-3">Direct Messages</h2>
        <p className="text-gray-400 mb-6">Sign in to send and receive private messages.</p>
        <button onClick={() => onNavigate('login')} className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors">
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-800 rounded w-48"></div>
          <div className="h-[600px] bg-gray-800/50 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Messages</h1>
        <button
          onClick={() => setShowNewConvo(true)}
          className="px-4 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-semibold transition-colors"
        >
          New Message
        </button>
      </div>

      {showNewConvo && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Start a conversation</h3>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter username..."
              value={newConvoUsername}
              onChange={(e) => setNewConvoUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStartConvo()}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-ink-500"
            />
            <button
              onClick={handleStartConvo}
              disabled={!newConvoUsername.trim()}
              className="px-4 py-2 bg-ink-600 hover:bg-ink-500 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              Start
            </button>
            <button
              onClick={() => { setShowNewConvo(false); setNewConvoError(''); }}
              className="px-3 py-2 text-gray-400 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
          {newConvoError && <p className="text-red-400 text-xs mt-2">{newConvoError}</p>}
        </div>
      )}

      <div className="flex bg-gray-900 border border-gray-800 rounded-xl overflow-hidden" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
        <div className={`w-full md:w-80 border-r border-gray-800 flex flex-col ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-gray-800">
            <h3 className="text-sm font-semibold text-gray-400">Conversations</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                No conversations yet. Start one by clicking "New Message" or visiting a user's profile.
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => loadMessages(c.id)}
                  className={`w-full p-3 text-left hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 flex items-center gap-3 ${
                    activeConvoId === c.id ? 'bg-gray-800/70' : ''
                  }`}
                >
                  {c.otherUser.avatarUrl ? (
                    <img src={c.otherUser.avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-ink-600 flex items-center justify-center text-sm font-bold">
                      {c.otherUser.username[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium truncate ${c.hasUnread ? 'text-white' : 'text-gray-300'}`}>
                        {c.otherUser.username}
                      </span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                        {c.lastMessage ? timeAgo(c.lastMessage.createdAt) : ''}
                      </span>
                    </div>
                    {c.lastMessage && (
                      <p className={`text-xs truncate mt-0.5 ${c.hasUnread ? 'text-gray-300 font-medium' : 'text-gray-500'}`}>
                        {c.lastMessage.senderId === user.id ? 'You: ' : ''}
                        {c.lastMessage.body.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').slice(0, 50)}
                      </p>
                    )}
                  </div>
                  {c.hasUnread && (
                    <div className="w-2.5 h-2.5 bg-ink-500 rounded-full flex-shrink-0"></div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div className={`flex-1 flex flex-col ${!mobileShowChat && !activeConvoId ? 'hidden md:flex' : 'flex'}`}>
          {activeConvoId && otherUser ? (
            <>
              <div className="p-3 border-b border-gray-800 flex items-center gap-3">
                <button
                  onClick={() => { setMobileShowChat(false); setActiveConvoId(null); }}
                  className="md:hidden text-gray-400 hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => onNavigate('profile', { username: otherUser.username })}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-1 min-w-0"
                >
                  {otherUser.avatarUrl ? (
                    <img src={otherUser.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-ink-600 flex items-center justify-center text-xs font-bold">
                      {otherUser.username[0].toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium text-sm truncate">{otherUser.username}</span>
                </button>
                {/* 3-dot conversation menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowConvoMenu(v => !v)}
                    className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
                    title="Conversation options"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" />
                    </svg>
                  </button>
                  {showConvoMenu && (
                    <div className="absolute right-0 top-9 w-44 bg-gray-800 border border-gray-700 rounded-xl shadow-xl py-1 z-50">
                      <button
                        onClick={() => {
                          onNavigate('profile', { username: otherUser.username });
                          setShowConvoMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                      >
                        View Profile
                      </button>
                      <hr className="border-gray-700 my-1" />
                      <button
                        onClick={() => {
                          setReportSent(true);
                          setShowConvoMenu(false);
                          setTimeout(() => setReportSent(false), 3000);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-amber-400 hover:bg-gray-700"
                      >
                        Report User
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Block ${otherUser.username}? You won't receive messages from them.`)) {
                            setActiveConvoId(null);
                            setOtherUser(null);
                            setMobileShowChat(false);
                            setConversations(prev => prev.filter(c => c.otherUser.username !== otherUser.username));
                          }
                          setShowConvoMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                      >
                        Block User
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {reportSent && (
                <div className="mx-4 mt-2 px-3 py-2 bg-amber-900/30 border border-amber-700/50 rounded-lg text-xs text-amber-400">
                  Report submitted. Our team will review this conversation.
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 text-sm py-10">
                    No messages yet. Say hello!
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMine = msg.senderId === user.id;
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${
                          isMine
                            ? 'bg-ink-600 text-white rounded-br-md'
                            : 'bg-gray-800 text-gray-200 rounded-bl-md'
                        }`}>
                          <p className="whitespace-pre-wrap break-words">{msg.body.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')}</p>
                          <p className={`text-[10px] mt-1 ${isMine ? 'text-ink-300' : 'text-gray-500'}`}>
                            {timeAgo(msg.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 border-t border-gray-800">
                <div className="flex gap-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    maxLength={5000}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white resize-none focus:outline-none focus:border-ink-500 max-h-32"
                    style={{ minHeight: '38px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                    className="px-4 py-2 bg-ink-600 hover:bg-ink-500 rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors flex-shrink-0"
                  >
                    {sending ? '...' : 'Send'}
                  </button>
                </div>
                {newMessage.length > 4500 && (
                  <p className="text-xs text-gray-500 mt-1 text-right">{newMessage.length}/5000</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              <div className="text-center">
                <div className="text-5xl mb-3">💬</div>
                <p>Select a conversation or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
